import React, { useMemo, useState } from "react";
import axios from "axios";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  formatPortalMoney,
} from "./useStudentPortalFetch";
import useStudentCourseSelection from "./useStudentCourseSelection";
import StudentCourseFilter from "./StudentCourseFilter";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import { resolveOrganizationId } from "../../Utils/organizationSession";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import {
  normalizeReceiptData,
} from "../EmsOptimized/List/feeReceiptUtils";
import { downloadEmsFeeReceiptPdf } from "../EmsOptimized/List/feeReceiptPdf";

function moneyDisplay(value, currencyCode) {
  return formatPortalMoney(value, currencyCode);
}

function resolveStatus(inst) {
  const raw = String(inst?.status || "").trim().toLowerCase();
  if (raw === "paid") return { key: "paid", label: "Paid" };
  if (raw === "cancelled") return { key: "cancelled", label: "Cancelled" };
  if (raw === "overdue") return { key: "overdue", label: "Overdue" };

  const due = inst?.due_date ? new Date(String(inst.due_date)) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due && !Number.isNaN(due.getTime())) {
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    if (dueDay > today) return { key: "upcoming", label: "Upcoming" };
    if (dueDay < today) return { key: "overdue", label: "Overdue" };
  }
  return { key: "pending", label: "Pending" };
}

function getStudentFees(inst) {
  if (Array.isArray(inst?.student_fees)) return inst.student_fees;
  if (Array.isArray(inst?.studentFees)) return inst.studentFees;
  return [];
}

async function fetchPortalFeeReceipt(studentFeeId, organizationId) {
  const res = await axios.get(
    `${MAIN_URL}/api/ems/portal/fees/${studentFeeId}/receipt`,
    {
      headers: getApiHeaders(),
      params: organizationId ? { organization_id: organizationId } : undefined,
    }
  );
  return normalizeReceiptData(res.data?.data ?? res.data);
}

export default function StudentFeesPage() {
  const { data, loading, error } = useStudentPortalFetch("fees");
  const {
    options,
    selected,
    selectedAdmissionId,
    setSelectedAdmissionId,
    showFilter,
    loading: coursesLoading,
    matchesSelection,
  } = useStudentCourseSelection();
  const { userData } = useAuthStore();
  const orgId = resolveOrganizationId(userData);

  const [receiptLoadingId, setReceiptLoadingId] = useState(null);
  const [receiptError, setReceiptError] = useState("");
  const [payNotice, setPayNotice] = useState("");

  const structures = useMemo(() => {
    const all = Array.isArray(data?.fee_structures) ? data.fee_structures : [];
    if (!selected) return all;
    return all.filter(matchesSelection);
  }, [data?.fee_structures, matchesSelection, selected]);

  const student = data?.student || {};
  const payments = useMemo(() => {
    const all = Array.isArray(data?.payments) ? data.payments : [];
    if (!selected) return all;
    return all.filter(matchesSelection);
  }, [data?.payments, matchesSelection, selected]);

  const installments = useMemo(() => {
    const all = Array.isArray(data?.installments) ? data.installments : [];
    if (!selected) return all;
    return all.filter(matchesSelection);
  }, [data?.installments, matchesSelection, selected]);

  const totals = useMemo(() => {
    const currency =
      structures[0]?.display_currency ||
      structures[0]?.currency_code ||
      data?.summary?.currency_code ||
      "INR";
    const totalCourseFee = structures.reduce(
      (sum, row) =>
        sum +
        Number(
          row.display_amount ??
            row.net_fee_amount_student_currency ??
            row.net_fee_amount ??
            0
        ),
      0
    );
    const totalPaid = payments.reduce((sum, row) => {
      const amount = Number(
        row.display_amount ??
          row.amount_student_currency ??
          row.amount_paid ??
          row.amount ??
          0
      );
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
    const outstanding = Math.max(0, totalCourseFee - totalPaid);
    return {
      totalCourseFee,
      totalPaid:
        selected || !data?.summary
          ? totalPaid
          : Number(data?.summary?.total_paid ?? data?.summary?.total_paid_inr ?? totalPaid),
      outstanding:
        selected || !data?.summary
          ? outstanding
          : Number(
              data?.summary?.outstanding ??
                Math.max(
                  0,
                  Number(data?.summary?.total_course_fee ?? totalCourseFee) -
                    Number(data?.summary?.total_paid ?? totalPaid)
                )
            ),
      currency,
    };
  }, [structures, payments, data?.summary, selected]);

  const trackerRows = useMemo(() => {
    const rows = [];
    structures.forEach((admission) => {
      const list = Array.isArray(admission.installments)
        ? admission.installments
        : [];
      list.forEach((inst) => {
        rows.push({
          ...inst,
          courseLabel: [
            admission.training_program_name,
            admission.category_name,
          ]
            .filter(Boolean)
            .join(" · "),
          admissionNumber: admission.admission_number,
          organization_ems_admission_id: admission.organization_ems_admission_id,
        });
      });
    });
    if (rows.length === 0) {
      installments.forEach((inst) => {
        rows.push({
          ...inst,
          student_fees: getStudentFees(inst),
          courseLabel:
            inst?.admission?.training_program?.training_program_name ||
            inst?.admission?.trainingProgram?.training_program_name ||
            "Training program",
          organization_ems_admission_id:
            inst?.organization_ems_admission_id ||
            inst?.admission?.organization_ems_admission_id,
        });
      });
    }
    return rows.sort(
      (a, b) =>
        Number(a.installment_number || 0) - Number(b.installment_number || 0)
    );
  }, [structures, installments]);

  const courseTags = useMemo(() => {
    const seen = new Set();
    const tags = [];
    structures.forEach((admission) => {
      const label = [admission.training_program_name, admission.category_name]
        .filter(Boolean)
        .join(" · ");
      if (label && !seen.has(label)) {
        seen.add(label);
        tags.push(label);
      }
    });
    return tags;
  }, [structures]);

  const hasAny =
    structures.length > 0 ||
    installments.length > 0 ||
    payments.length > 0;

  const loadReceipt = async (studentFeeId) => {
    setReceiptError("");
    setReceiptLoadingId(studentFeeId);
    try {
      const payload = await fetchPortalFeeReceipt(studentFeeId, orgId);
      if (!payload) throw new Error("Could not load fee receipt.");
      return payload;
    } catch (err) {
      setReceiptError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load fee receipt."
      );
      return null;
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const handleDownloadReceipt = async (studentFeeId) => {
    const ready = await loadReceipt(studentFeeId);
    if (!ready) return;
    const safe = String(ready.receiptNo || studentFeeId).replace(
      /[^\w.-]+/g,
      "_"
    );
    await downloadEmsFeeReceiptPdf(ready, `fee-receipt-${safe}.pdf`);
  };

  const handlePayNow = (inst) => {
    setPayNotice(
      `Installment ${inst.installment_number || ""} · ${moneyDisplay(
        inst.display_amount ??
          inst.fee_instalment_student_student_currency ??
          inst.fee_instalment_inr,
        inst.display_currency || inst.student_currency_code || totals.currency
      )} due on ${formatPortalDate(inst.due_date) || "—"}. Online payment is not enabled yet — please contact your institute to complete this payment.`
    );
  };

  return (
    <StudentPortalShell
      title="My Fee"
      tags={
        courseTags.length > 0
          ? courseTags.map((tag) => (
              <span className="ems-fee-course-tag" key={tag}>
                {tag}
              </span>
            ))
          : null
      }
      headerRight={
        <div className="ems-fee-header-actions">
          <StudentCourseFilter
            options={options}
            value={selectedAdmissionId}
            onChange={setSelectedAdmissionId}
            showFilter={showFilter}
            loading={coursesLoading}
          />
          <div className="ems-fee-student-chip">
            <span className="ems-fee-student-chip-name">
              {(student.name || "Student").replace(/\b\w/g, (c) =>
                c.toUpperCase()
              )}
            </span>
            <span className="ems-fee-student-chip-id">
              {student.student_id || "—"}
            </span>
          </div>
        </div>
      }
    >
      <StudentPortalState
        loading={loading}
        error={error}
        empty={!loading && !error && !hasAny}
      >
        <div className="ems-student-fees ems-fee-status-first">
          <section className="ems-fee-summary-cards">
            <div className="ems-fee-summary-card">
              <span className="ems-fee-summary-label">Total Course Fee</span>
              <strong className="ems-fee-summary-value">
                {moneyDisplay(totals.totalCourseFee, totals.currency)}
              </strong>
            </div>
            <div className="ems-fee-summary-card ems-fee-summary-paid">
              <span className="ems-fee-summary-label">Total Paid</span>
              <strong className="ems-fee-summary-value">
                {moneyDisplay(totals.totalPaid, totals.currency)}
              </strong>
            </div>
            <div className="ems-fee-summary-card ems-fee-summary-due">
              <span className="ems-fee-summary-label">Outstanding Balance</span>
              <strong className="ems-fee-summary-value">
                {moneyDisplay(totals.outstanding, totals.currency)}
              </strong>
            </div>
          </section>

          <section className="ems-student-section">
            <div className="ems-fee-tracker-heading">
              <h3 className="ems-fee-tracker-title">Installment Tracker</h3>
              <p className="ems-student-meta">
                Payment timeline for your admitted courses
              </p>
            </div>

            <div className="ems-student-table-wrap">
              <table className="ems-student-table ems-fee-tracker-table">
                <thead>
                  <tr>
                    <th>Installment</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerRows.length === 0 ? (
                    <tr>
                      <td colSpan={5}>No installments found.</td>
                    </tr>
                  ) : (
                    trackerRows.map((inst) => {
                      const status = resolveStatus(inst);
                      const sfs = getStudentFees(inst);
                      const isPaid = status.key === "paid";
                      const canPay =
                        status.key === "pending" || status.key === "overdue";

                      return (
                        <tr key={inst.organization_ems_fee_installment_id}>
                          <td data-label="Installment">
                            <div className="ems-fee-installment-cell">
                              <strong>
                                Installment {inst.installment_number ?? "—"}
                              </strong>
                              {inst.courseLabel ? (
                                <span className="ems-student-meta">
                                  {inst.courseLabel}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td data-label="Due Date">
                            {formatPortalDate(inst.due_date) || "—"}
                          </td>
                          <td data-label="Amount">
                            {moneyDisplay(
                              inst.display_amount ??
                                inst.fee_instalment_student_student_currency ??
                                inst.fee_instalment_inr,
                              inst.display_currency ||
                                inst.student_currency_code ||
                                totals.currency
                            )}
                          </td>
                          <td data-label="Status">
                            <span
                              className={`ems-fee-status-pill ems-fee-status-${status.key}`}
                            >
                              <span className="ems-fee-status-dot" />
                              {status.label}
                            </span>
                          </td>
                          <td data-label="Action">
                            {isPaid && sfs.length > 0 ? (
                              <div className="ems-fee-receipt-actions">
                                {sfs.map((sf) => {
                                  const feeId =
                                    sf.organization_ems_student_fee_id;
                                  const busy =
                                    String(receiptLoadingId) === String(feeId);
                                  return (
                                    <button
                                      key={feeId}
                                      type="button"
                                      className="ems-student-btn ems-student-btn-primary"
                                      disabled={busy}
                                      onClick={() =>
                                        handleDownloadReceipt(feeId)
                                      }
                                    >
                                      {busy
                                        ? "Loading…"
                                        : "Receipt"}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : canPay ? (
                              <button
                                type="button"
                                className="ems-student-btn ems-student-btn-pay"
                                onClick={() => handlePayNow(inst)}
                              >
                                Pay Now
                              </button>
                            ) : (
                              <span className="ems-student-meta">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {payNotice ? (
              <div className="ems-fee-pay-notice">
                <p>{payNotice}</p>
                <button
                  type="button"
                  className="ems-student-btn ems-student-btn-secondary"
                  onClick={() => setPayNotice("")}
                >
                  Dismiss
                </button>
              </div>
            ) : null}
            {receiptError ? (
              <p className="ems-student-portal-error">{receiptError}</p>
            ) : null}
          </section>
        </div>
      </StudentPortalState>
    </StudentPortalShell>
  );
}
